import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  FormHelperText,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { emailTemplateService } from "../../utils/services/email-templateService";
import CustomTypography from "../../globalStyles/fonts";

const EmailTemplateModal = ({
  open,
  handleClose,
  onAddData,
  selectedTemplate,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    content: "",
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (selectedTemplate) {
      setFormData({
        name: selectedTemplate.name || "",
        subject: selectedTemplate.subject || "",
        content: selectedTemplate.content || "",
      });
    } else {
      setFormData({ name: "", subject: "", content: "" });
    }
    setFormErrors({});
  }, [selectedTemplate]);

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Template name is required";
    if (!formData.subject.trim())
      errors.subject = "Template subject is required";
    if (!formData.content.trim())
      errors.content = "Template content is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddTemplate = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: formData.name,
      subject: formData.subject,
      content: formData.content,
    };

    emailTemplateService
      .create(payload)
      .then(() => {
        toast.success("Template created successfully!");
        onAddData && onAddData();
        handleClose();
      })
      .catch(() => {
        toast.error("Failed to create template.");
      });
  };

  const handleEditTemplate = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      name: formData.name,
      subject: formData.subject,
      content: formData.content,
    };

    emailTemplateService
      .edit(selectedTemplate._id, payload)
      .then(() => {
        toast.success("Template updated successfully!");
        onAddData && onAddData();
        handleClose();
      })
      .catch(() => {
        toast.error("Failed to update template.");
      });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md">
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <DialogTitle>
          {selectedTemplate ? "Edit Email Template" : "Create Email Template"}
        </DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider />
      <DialogContent>
        <Grid container spacing={2}>
          <CustomTypography
            style={{
              fontSize: 14,
              fontFamily: "Poppins",
              fontWeight: 400,
              color: "#11193F",
            }}
          >
            Template Name <span style={{ color: "red" }}>*</span>
          </CustomTypography>
          <Grid item xs={12}>
            <TextField
              size="small"
              margin="dense"
              name="name"
              fullWidth
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTypography
              style={{
                fontSize: 14,
                fontFamily: "Poppins",
                fontWeight: 400,
                color: "#11193F",
              }}
            >
              Template Subject <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <TextField
              size="small"
              margin="dense"
              name="subject"
              fullWidth
              value={formData.subject}
              onChange={(e) =>
                setFormData({ ...formData, subject: e.target.value })
              }
              error={!!formErrors.subject}
              helperText={formErrors.subject}
            />
          </Grid>
          <Grid item xs={12}>
            <CustomTypography
              style={{
                fontSize: 14,
                fontFamily: "Poppins",
                fontWeight: 400,
                color: "#11193F",
              }}
            >
              Template Content <span style={{ color: "red" }}>*</span>
            </CustomTypography>
            <ReactQuill
              style={{ height: 150, marginTop: 10 }}
              value={formData.content}
              onChange={(value) => setFormData({ ...formData, content: value })}
            />
            {formErrors.content && (
              <Typography color="error" variant="body2" sx={{ mt: 7 }}>
                {formErrors.content}
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={(e) =>
            selectedTemplate ? handleEditTemplate(e) : handleAddTemplate(e)
          }
          color="primary"
          variant="contained"
        >
          {selectedTemplate ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailTemplateModal;
